/**
 * Tracks whether this session's identity is known in full (signed in with every identity
 * field resolved, or explicitly signed out) at least once. `GtmProvider` waits on this
 * before injecting the GTM script, so the Google Tag's one-time Initialization trigger -
 * and the `page_view` it sends - always carries every User Property, never a partial or
 * empty set.
 *
 * There is deliberately no timeout: a signed-in session whose identity never resolves
 * loads no GTM at all rather than sending page views without identity. Signed-out and
 * public pages resolve through `markShiOneIdentityAbsent`, which each app calls only when
 * it knows there is no user.
 */
let resolved = false;
let callbacks: Array<() => void> = [];

/** Marks identity as known and runs any callbacks waiting on it. Idempotent. */
export function markIdentityResolved(): void {
  if (resolved) return;
  resolved = true;
  const pending = callbacks;
  callbacks = [];
  pending.forEach((callback) => callback());
}

/**
 * Runs `callback` once identity is known - immediately if it already is, otherwise when
 * `markIdentityResolved` is next called. Returns an unsubscribe function; call it if the
 * caller may be torn down before identity resolves, so a stale closure (e.g. an unmounted
 * `GtmProvider`'s captured props) never fires.
 */
export function onIdentityResolved(callback: () => void): () => void {
  if (resolved) {
    callback();
    return () => {};
  }
  callbacks.push(callback);
  return () => {
    callbacks = callbacks.filter((c) => c !== callback);
  };
}

/** Test-only: resets readiness state between test cases. */
export function resetIdentityReadinessForTests(): void {
  resolved = false;
  callbacks = [];
}
